import type { MemberDataFirestoreRecord } from "@albertine/types";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { type CatalogItem, getCatalogItemKeyDetail } from "../../../common/CatalogItem";
import type { Request } from "../../../common/types/Request";
import { excludeUndefined } from "../../../common/utils/array.util";
import { cdnImage } from "../../../common/utils/image.util";
import { singularPlural } from "../../../common/utils/time.formatted.util";
import { EventEmitter, type EventListener } from "../../../EventEmitter";
import { nameWithoutTitle } from "../../../utils/members.util";
import routes from "../../../utils/routes";
import { scrollRefToTop } from "../../../utils/window.util";
import Asset from "../../Common/Asset";
import Button from "../../Common/Buttons";
import { Flex } from "../../Common/Flex";
import Icon from "../../Common/Icon";
import InlineTextDelimiter from "../../Common/InlineTextDelimiter";
import Label from "../../Common/Label";
import type { BucketProps } from "../../Common/Overlay/FullScreenOverlay";
import ImageBalloons from "./ImageBalloons";
import "./ProactivityCart.css";
import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { categoryDisplayName } from "../../../common/types/OfferingCategory";

function Title(props: {
    member: MemberDataFirestoreRecord;
    request: Request | undefined;
}) {
    const { member, request } = props;
    const titleParts = [nameWithoutTitle(member), request?.title].filter(
        excludeUndefined,
    );

    return (
        <Label.Medium.N10>
            {titleParts.map((titlePart, index) => (
                <>
                    {titlePart}
                    {titleParts[index + 1] && <InlineTextDelimiter />}
                </>
            ))}
        </Label.Medium.N10>
    );
}

type ProactivityCartProps = {
    request: Request | undefined;
    member: MemberDataFirestoreRecord;
    initialItems: CatalogItemFull[];
} & BucketProps;

export default function ProactivityCart(props: ProactivityCartProps) {
    const { request, member, initialItems, size, maximize, minimize } = props;
    const [catalogItems, setCatalogItems] = useState<CatalogItemFull[]>(initialItems);
    const scrollElementRef = useRef<HTMLDivElement>(null);
    const history = useHistory();

    function goToViewMode(catalogItemId: string) {
        history.push(
            routes.catalogSearch("proactivity", request?.id, member.id, catalogItemId),
        );
    }

    function addItem(newItem: CatalogItemFull) {
        const updated = [
            // Update existing item
            ...catalogItems.map((oldItem) =>
                oldItem.id === newItem.id ? newItem : oldItem,
            ),
            // Add new item
            catalogItems.map((it) => it.id).includes(newItem.id) ? undefined : newItem,
        ].filter(excludeUndefined);
        setCatalogItems(updated);
    }

    useEffect(() => {
        scrollRefToTop(scrollElementRef);
    }, []);

    useEffect(() => {
        const addCatalogItemToChatAttachments: EventListener = {
            name: "addCatalogItemToChatAttachments",
            listener: (params) => {
                addItem(params.item);
            },
        };

        EventEmitter.addListener(addCatalogItemToChatAttachments);

        return () => {
            EventEmitter.removeListener(addCatalogItemToChatAttachments);
        };
    }, [catalogItems]);

    if (catalogItems.length === 0) {
        return null;
    }

    const header = (
        <Flex.Column gap="small">
            <Title member={member} request={request} />
            <Label.Small.N50>
                {singularPlural(
                    `${catalogItems.length} article added`,
                    `${catalogItems.length} articles added`,
                    catalogItems.length,
                )}
            </Label.Small.N50>
        </Flex.Column>
    );

    if (size === "minimized") {
        return (
            <Flex.Column
                className="proactivity-cart--minimized"
                alignItems="start"
                gap="large"
                onClick={maximize}
            >
                <ImageBalloons
                    images={catalogItems.map((it) =>
                        it.images ? it.images[0] : { id: it.id, url: null },
                    )}
                    showMax={5}
                />
                {header}
            </Flex.Column>
        );
    }

    return (
        <Flex.Column alignItems="start">
            <Flex.Row
                alignItems="start"
                gap="large"
                className="proactivity-cart-header"
            >
                {header}
                <Button.Ghost onClick={minimize}>
                    <Icon.Close.Medium />
                </Button.Ghost>
            </Flex.Row>
            <Flex.Column
                alignItems="start"
                className="proactivity-cart__items"
                ref={scrollElementRef}
            >
                {catalogItems.map((item) => (
                    <Flex.Row
                        key={item.id}
                        className="proactivity-cart__item"
                        alignItems="start"
                        gap="large"
                        onClick={() => {
                            goToViewMode(item.id);
                        }}
                    >
                        <div className="proactivity-cart__item-thumbnail">
                            {item.images?.[0] && (
                                <Asset
                                    src={cdnImage(item.images[0].url, {
                                        height: 64,
                                        width: 64,
                                    })}
                                    className="proactivity-cart__item-image"
                                />
                            )}
                        </div>
                        <Flex.Column
                            gap="small"
                            justifyContent="start"
                            className="proactivity-cart__item-details"
                        >
                            <h4>{item.name}</h4>

                            {item.offeringCategories?.[0] && (
                                <Label.Medium.Light.N50>
                                    {categoryDisplayName(
                                        item.offeringCategories[0],
                                        "en",
                                    )}
                                </Label.Medium.Light.N50>
                            )}

                            <Label.Small>
                                {getCatalogItemKeyDetail(item) || item.address}
                            </Label.Small>
                        </Flex.Column>
                    </Flex.Row>
                ))}
            </Flex.Column>
        </Flex.Column>
    );
}
